// apps/web/src/components/decision-form/DecisionFormEditor.tsx
/**
 * @file DecisionFormEditor.tsx
 * @description ZHSR07 驗證決定表單填寫器。按 scheme 渲染一~五節，供審議成員填寫。
 *   §5.3 追蹤對策 checklist（followUpActions）放在「三、決定結果」節風險等級下方。
 */
import { Card, Checkbox, Input, Radio, Space, Typography } from 'antd';
import { getDecisionFormDefs, formatConclusionLabel, type DecisionScheme } from '@zhcb/shared';
import { CertInfoCard } from './CertInfoCard';

/**
 * §5.3 追蹤對策欄位。
 * 與後端 verification_decisions.followUpActions jsonb 欄位一一對應。
 */
export interface FollowUpActions {
  /** 勾選的對策項 key 列表 */
  items: string[];
  /** 增加不定期追查：執行時間（月內） */
  irregularMonths?: string;
  /** 增加不定期追查：件數 */
  irregularCount?: string;
  /** 列入後市場抽驗計畫：執行時間（月內） */
  postMarketMonths?: string;
  /** 列入後市場抽驗計畫：件數 */
  postMarketCount?: string;
  /** 「不影響決定但有其他建議改善措施」的說明 */
  note?: string;
}

/** 追蹤對策選項定義 */
const FOLLOW_UP_OPTIONS: { key: string; label: string }[] = [
  { key: 'na', label: '不適用' },
  { key: 'periodic', label: '以定期追蹤查驗方式持續監控' },
  { key: 'irregular', label: '增加不定期追查（包括不預警）' },
  { key: 'addManDays', label: '於下次定期追查增加人天數' },
  { key: 'addSamples', label: '於下次定期追查增加抽驗樣品數量' },
  { key: 'postMarket', label: '列入後市場抽驗計畫' },
  { key: 'other', label: '不影響驗證決定結果，但有其他的建議改善措施' },
];

export interface DecisionFormValue {
  /** { sectionKey: { itemKey: { result, note } } } */
  formContent: Record<string, Record<string, { result?: string; note?: string }>>;
  deliberationNotes: string;
  conclusion?: string;
  conclusionNote?: string;
  riskLevel?: '低' | '中' | '高';
  certInfo: Record<string, string>;
  /** §5.3 追蹤對策 checklist */
  followUpActions: FollowUpActions;
}

interface Props {
  scheme: DecisionScheme;
  verificationType: string;
  value: DecisionFormValue;
  onChange: (next: DecisionFormValue) => void;
  editable: boolean;
}

export function DecisionFormEditor({ scheme, verificationType, value, onChange, editable }: Props) {
  const defs = getDecisionFormDefs(scheme);

  const setItem = (
    sectionKey: string,
    itemKey: string,
    patch: { result?: string; note?: string },
  ) => {
    const section = { ...(value.formContent[sectionKey] ?? {}) };
    section[itemKey] = { ...(section[itemKey] ?? {}), ...patch };
    onChange({ ...value, formContent: { ...value.formContent, [sectionKey]: section } });
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }} size={16}>
      {/* 審議記錄卡片已依客戶 2026-07 需求移除（deliberationNotes 欄位保留於 DB，僅隱藏 UI）。 */}

      {/* 一~三節 checklist */}
      {defs.sections.map((section) => (
        <Card key={section.key} title={section.title} size="small">
          <Space direction="vertical" style={{ width: '100%' }} size={12}>
            {section.items.map((item) => {
              const cur = value.formContent[section.key]?.[item.key] ?? {};
              return (
                <div key={item.key}>
                  {/* label 內含換行（多層 a) b) 細項），以 pre-line 保留段落結構 */}
                  <Typography.Paragraph style={{ marginBottom: 4, whiteSpace: 'pre-line' }}>
                    {item.label}
                  </Typography.Paragraph>
                  <Radio.Group
                    disabled={!editable}
                    value={cur.result}
                    onChange={(e) => setItem(section.key, item.key, { result: e.target.value })}
                  >
                    {item.options.map((o) => (
                      <Radio key={o} value={o}>
                        {o}
                      </Radio>
                    ))}
                  </Radio.Group>
                  <Input
                    style={{ marginTop: 4 }}
                    disabled={!editable}
                    placeholder="備註"
                    value={cur.note ?? ''}
                    onChange={(e) => setItem(section.key, item.key, { note: e.target.value })}
                  />
                </div>
              );
            })}
            {/* 風險等級只在「三、決定結果」節顯示 */}
            {section.key === 'result' && (
              <div>
                <Typography.Text strong>風險評估等級</Typography.Text>
                <Radio.Group
                  style={{ display: 'block', marginTop: 4 }}
                  disabled={!editable}
                  value={value.riskLevel}
                  onChange={(e) => onChange({ ...value, riskLevel: e.target.value })}
                >
                  <Radio value="低">低</Radio>
                  <Radio value="中">中</Radio>
                  <Radio value="高">高</Radio>
                </Radio.Group>
              </div>
            )}
            {/* 追蹤對策 checklist（§5.3），只在「三、決定結果」節顯示 */}
            {section.key === 'result' &&
              (() => {
                /** 以 ?? 兜底，避免初始值 undefined 時崩潰 */
                const fa: FollowUpActions = value.followUpActions ?? { items: [] };
                const checkedItems = new Set(fa.items);

                /**
                 * 更新 followUpActions 的某個欄位，不可變地合併後回傳。
                 */
                const setFa = (patch: Partial<FollowUpActions>) =>
                  onChange({ ...value, followUpActions: { ...fa, ...patch } });

                return (
                  <div>
                    <Typography.Text strong>追蹤對策</Typography.Text>
                    <div style={{ marginTop: 8 }}>
                      <Checkbox.Group
                        disabled={!editable}
                        value={fa.items}
                        onChange={(keys) => setFa({ items: keys.map(String) })}
                      >
                        <Space direction="vertical" size={8}>
                          {FOLLOW_UP_OPTIONS.map((opt) => (
                            <Checkbox key={opt.key} value={opt.key}>
                              {opt.label}
                            </Checkbox>
                          ))}
                        </Space>
                      </Checkbox.Group>

                      {/* 增加不定期追查：附加輸入欄 */}
                      {checkedItems.has('irregular') && (
                        <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                          <span style={{ lineHeight: '32px', whiteSpace: 'nowrap' }}>執行時間</span>
                          <Input
                            style={{ width: 80 }}
                            disabled={!editable}
                            placeholder="月數"
                            suffix="月內"
                            value={fa.irregularMonths ?? ''}
                            onChange={(e) => setFa({ irregularMonths: e.target.value })}
                          />
                          <span style={{ lineHeight: '32px', whiteSpace: 'nowrap' }}>件數</span>
                          <Input
                            style={{ width: 80 }}
                            disabled={!editable}
                            placeholder="件數"
                            value={fa.irregularCount ?? ''}
                            onChange={(e) => setFa({ irregularCount: e.target.value })}
                          />
                        </div>
                      )}

                      {/* 列入後市場抽驗計畫：附加輸入欄 */}
                      {checkedItems.has('postMarket') && (
                        <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                          <span style={{ lineHeight: '32px', whiteSpace: 'nowrap' }}>執行時間</span>
                          <Input
                            style={{ width: 80 }}
                            disabled={!editable}
                            placeholder="月數"
                            suffix="月內"
                            value={fa.postMarketMonths ?? ''}
                            onChange={(e) => setFa({ postMarketMonths: e.target.value })}
                          />
                          <span style={{ lineHeight: '32px', whiteSpace: 'nowrap' }}>件數</span>
                          <Input
                            style={{ width: 80 }}
                            disabled={!editable}
                            placeholder="件數"
                            value={fa.postMarketCount ?? ''}
                            onChange={(e) => setFa({ postMarketCount: e.target.value })}
                          />
                        </div>
                      )}

                      {/* 其他建議改善措施：說明欄 */}
                      {checkedItems.has('other') && (
                        <Input.TextArea
                          style={{ marginTop: 8 }}
                          rows={3}
                          disabled={!editable}
                          placeholder="請說明其他建議改善措施..."
                          value={fa.note ?? ''}
                          onChange={(e) => setFa({ note: e.target.value })}
                        />
                      )}
                    </div>
                  </div>
                );
              })()}
          </Space>
        </Card>
      ))}

      {/* 四、證書資訊 */}
      <CertInfoCard
        certFields={defs.certFields}
        verificationType={verificationType}
        value={value.certInfo}
        onChange={(next) => onChange({ ...value, certInfo: next })}
        editable={editable}
      />

      {/* 五、決定結論 */}
      <Card title="五、驗證決定結論" size="small">
        <Radio.Group
          disabled={!editable}
          value={value.conclusion}
          onChange={(e) => onChange({ ...value, conclusion: e.target.value })}
        >
          <Space direction="vertical">
            {defs.conclusions.map((c) => (
              <Radio key={c} value={c}>
                {formatConclusionLabel(c)}
              </Radio>
            ))}
          </Space>
        </Radio.Group>
        {value.conclusion === '其他' && (
          <Input.TextArea
            style={{ marginTop: 8 }}
            rows={2}
            disabled={!editable}
            placeholder="其他結論說明"
            value={value.conclusionNote ?? ''}
            onChange={(e) => onChange({ ...value, conclusionNote: e.target.value })}
          />
        )}
      </Card>
    </Space>
  );
}
